import { useNavigate } from 'react-router-dom';

import nf from '@/constants/nf';
import { route } from '@/constants/routeName';
import {
	canOrderForChild,
	dashboardStatusLabel,
	servingTimeDisplayLabel,
	visibleServingTimeLines,
} from '@/helper/dashboardStatus';
import useSwitchChild from '@/hooks/useSwitchChild';

/**
 * One child on the family dashboard.
 * Order / View orders use the shared switchChild handler (same as header selector).
 */
export default function ChildCard({ child }) {
	const navigate = useNavigate();
	const { switchChild, switchingLocked } = useSwitchChild();

	const name = child.member?.name || `Child #${child.id}`;
	const studentId = child.member?.on_list_attributes?.student_id;
	const klass = child.member?.on_list_attributes?.class;
	const status = child.status || (child.is_active ? 'not_ordered' : 'inactive');
	const statusLabel = dashboardStatusLabel(status);
	const allowOrder = canOrderForChild(child);
	const servingTimes = visibleServingTimeLines(child.serving_times);

	const onOrder = () => {
		const result = switchChild(child.id);
		if (!result.ok) return;
		navigate(`/${route.menu}`);
	};

	const onViewOrders = () => {
		const result = switchChild(child.id);
		if (!result.ok) return;
		navigate(`/${route.orders}`);
	};

	return (
		<article className='flex flex-col gap-3 rounded-md border border-black bg-white p-4'>
			<div className='flex items-start justify-between gap-2'>
				<div className='min-w-0 flex-1 flex flex-col gap-1'>
					<h2 className='text-base font-bold text-black'>{name}</h2>
					<p className='text-sm text-[var(--weak)]'>
						{studentId ? `Student ID ${studentId}` : 'Student ID —'}
						{klass ? ` · ${klass}` : ''}
					</p>
				</div>
				<span className='shrink-0 rounded border border-black px-2 py-0.5 text-xs font-semibold text-black'>
					{statusLabel}
				</span>
			</div>

			<p className='text-sm'>
				<span className='text-[var(--weak)]'>Deposit:</span>{' '}
				<span className='font-bold text-black'>{nf(+child.balance || 0)}</span>
			</p>

			{servingTimes.length > 0 && (
				<p className='text-sm text-black'>
					{servingTimes.map((st, index) => (
						<span key={`${child.id}-${st.name}-${index}`}>
							{index > 0 ? ' · ' : null}
							{servingTimeDisplayLabel(st)}
						</span>
					))}
				</p>
			)}

			<div className='flex flex-col gap-2'>
				{allowOrder && (
					<button
						type='button'
						className='btn-actions h-11 w-full rounded-md px-4 text-white'
						disabled={switchingLocked}
						onClick={onOrder}
					>
						Order for this child
					</button>
				)}
				<button
					type='button'
					className='h-9 w-full text-sm font-semibold text-black underline underline-offset-2 transition-opacity duration-100 active:opacity-80 disabled:opacity-70'
					disabled={switchingLocked}
					onClick={onViewOrders}
				>
					View orders
				</button>
			</div>
		</article>
	);
}
